<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- 周期类型和天数 -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          周期类型 <span class="text-red-500">*</span>
        </label>
        <select v-model="formData.durationType" required :disabled="isEdit"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
          <option value="" disabled>请选择周期类型</option>
          <option v-for="option in durationTypeOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <p v-if="isEdit" class="mt-1 text-xs text-gray-500 dark:text-gray-400">编辑模式下不可修改</p>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          周期天数 <span class="text-red-500">*</span>
        </label>
        <input v-model.number="formData.durationDays" type="number" required min="1" :disabled="isDurationDaysDisabled"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
          placeholder="例如: 30" />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ durationDaysHint }}</p>
      </div>
    </div>

    <!-- 折扣百分比 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        折扣百分比
      </label>
      <input v-model.number="discountPercentageInput" type="number" min="0" max="1" step="0.01"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
        placeholder="例如: 0.88 表示 88 折" />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">0-1 之间的小数，例如 0.88 表示 88 折（默认 0.88）</p>
    </div>

    <!-- 价格、原价和结算底价 -->
    <div class="grid grid-cols-3 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          价格（元）<span class="text-red-500">*</span>
        </label>
        <input v-model.number="priceInYuan" type="number" required min="0" step="0.01"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
          placeholder="例如: 99.00" />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">平台售价</p>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          结算底价（元）<span class="text-red-500">*</span>
        </label>
        <input v-model.number="settlementPriceInYuan" type="number" required min="0" step="0.01"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
          placeholder="例如: 60.00" />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">租户售价不得低于此值</p>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          原价（元）
        </label>
        <input v-model.number="originalPriceInYuan" type="number" min="0" step="1"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-600"
          placeholder="根据价格和折扣自动计算" readonly />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">自动计算（价格 ÷ 折扣）</p>
      </div>
    </div>

    <!-- 一次性套餐选项 -->
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      <div class="flex items-center">
        <input v-model="formData.isOneTime" type="checkbox" id="isOneTime" :disabled="isEdit"
          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded disabled:cursor-not-allowed" />
        <label for="isOneTime" class="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          一次性套餐
        </label>
        <span v-if="isEdit" class="ml-2 text-xs text-gray-500 dark:text-gray-400">（编辑模式下不可修改）</span>
      </div>
      <div v-if="formData.isOneTime">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          最大购买次数
        </label>
        <input v-model.number="formData.maxPurchaseCount" type="number" min="1" :disabled="isEdit"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
          placeholder="例如: 1" />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">用户最多可购买的次数{{ isEdit ? '（编辑模式下不可修改）' : '' }}</p>
      </div>
    </div>

    <!-- 显示顺序 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        显示顺序
      </label>
      <input v-model.number="formData.displayOrder" type="number" min="0"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
        placeholder="例如: 0" />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">数字越小越靠前，默认为 0</p>
    </div>

    <!-- 状态选项 -->
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      <div class="flex items-center">
        <input v-model="formData.isHot" type="checkbox" id="isHot"
          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded" />
        <label for="isHot" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
          标记为热门
        </label>
      </div>
      <div class="flex items-center">
        <input v-model="formData.isHidden" type="checkbox" id="isHidden"
          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded" />
        <label for="isHidden" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
          隐藏此价格方案
        </label>
        <span class="ml-2 text-xs text-gray-500 dark:text-gray-400">（隐藏后用户端不可见）</span>
      </div>
    </div>

    <!-- 按钮 -->
    <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
      <button type="button" @click="handleCancel"
        class="cancel-btn">
        取消
      </button>
      <button type="submit" class="primary-btn">
        {{ isEdit ? '更新' : '创建' }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import type { PlanPricing, CreatePricingRequest, UpdatePricingRequest } from '@/types/membership/pricing'
import { getDurationTypeOptions, DurationType } from '@/types/membership/enums'

/**
 * Props
 */
interface Props {
  /** 价格方案（编辑模式时传入） */
  pricing?: PlanPricing
  /** 套餐 ID（创建模式时传入） */
  planId?: number
  /** 是否为编辑模式 */
  isEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
})

/**
 * Emits
 */
const emit = defineEmits<{
  /** 提交表单 */
  submit: [data: CreatePricingRequest | UpdatePricingRequest]
  /** 取消操作 */
  cancel: []
}>()

/**
 * 周期类型选项
 */
const durationTypeOptions = getDurationTypeOptions()

/**
 * 周期类型和天数的映射关系
 */
const durationDaysMap: Record<DurationType, number> = {
  [DurationType.MONTHLY]: 30,
  [DurationType.QUARTERLY]: 90,
  [DurationType.YEARLY]: 365,
  [DurationType.CUSTOM]: 7, // 自定义默认 7 天
}

/**
 * 表单数据（以"分"为单位存储）
 */
const formData = ref({
  durationType: DurationType.MONTHLY,
  durationDays: 30,
  price: 0,
  originalPrice: null as number | null,
  discountPercentage: 0.88, // 默认折扣 0.88
  settlementPrice: 0,
  isHot: false,
  isOneTime: false,
  maxPurchaseCount: null as number | null,
  displayOrder: 0,
  isHidden: false,
})

/**
 * 价格输入（以"元"为单位）
 */
const priceInYuan = ref<number>(0)
const originalPriceInYuan = ref<number | null>(null)
const settlementPriceInYuan = ref<number>(0)
const discountPercentageInput = ref<number>(0.88) // 默认折扣 0.88

/**
 * 监听价格输入，转换为"分"
 */
watch(priceInYuan, (newValue) => {
  formData.value.price = Math.round(newValue * 100)
  // 如果结算底价为0，自动设置为价格
  if (settlementPriceInYuan.value === 0) {
    settlementPriceInYuan.value = newValue
  }
})

watch(originalPriceInYuan, (newValue) => {
  formData.value.originalPrice = newValue ? Math.round(newValue * 100) : null
})

watch(settlementPriceInYuan, (newValue) => {
  formData.value.settlementPrice = Math.round(newValue * 100)
})

watch(discountPercentageInput, (newValue) => {
  formData.value.discountPercentage = newValue
})

/**
 * 监听价格和折扣变化，自动计算原价
 * 原价 = 价格 / 折扣（向上取整）
 */
watch([priceInYuan, discountPercentageInput], ([price, discount]) => {
  if (price > 0 && discount > 0 && discount <= 1) {
    // 根据价格和折扣计算原价，向上取整
    const calculatedOriginalPrice = price / discount
    originalPriceInYuan.value = Math.ceil(calculatedOriginalPrice) // 向上取整为整数
  } else {
    originalPriceInYuan.value = null
  }
})

/**
 * 判断周期天数输入框是否禁用
 */
const isDurationDaysDisabled = computed(() => {
  // 编辑模式下，所有周期类型都不可修改
  if (props.isEdit) {
    return true
  }
  // 创建模式下，非自定义周期不可修改
  return formData.value.durationType !== DurationType.CUSTOM
})

/**
 * 周期天数提示文案
 */
const durationDaysHint = computed(() => {
  if (props.isEdit) {
    return '编辑模式下不可修改'
  }
  if (formData.value.durationType === DurationType.CUSTOM) {
    return '请输入自定义天数'
  }
  return '自动填充，不可修改'
})

/**
 * 监听周期类型变化，自动填充天数
 */
watch(
  () => formData.value.durationType,
  (newType) => {
    // 只在创建模式下自动填充
    if (!props.isEdit) {
      const defaultDays = durationDaysMap[newType]
      if (defaultDays !== undefined) {
        formData.value.durationDays = defaultDays
      }
    }
  }
)

/**
 * 监听 pricing prop 变化，用于编辑模式
 */
watch(
  () => props.pricing,
  (newPricing) => {
    if (newPricing) {
      formData.value = {
        durationType: newPricing.durationType,
        durationDays: newPricing.durationDays,
        price: newPricing.price,
        originalPrice: newPricing.originalPrice,
        discountPercentage: newPricing.discountPercentage ?? 0.88,
        settlementPrice: newPricing.settlementPrice ?? newPricing.price,
        isHot: newPricing.isHot,
        isOneTime: newPricing.isOneTime,
        maxPurchaseCount: newPricing.maxPurchaseCount,
        displayOrder: newPricing.displayOrder,
        isHidden: newPricing.isHidden,
      }
      // 转换为"元"显示
      priceInYuan.value = newPricing.price / 100
      originalPriceInYuan.value = newPricing.originalPrice ? newPricing.originalPrice / 100 : null
      settlementPriceInYuan.value = (newPricing.settlementPrice ?? newPricing.price) / 100
      discountPercentageInput.value = newPricing.discountPercentage ?? 0.88
    }
  },
  { immediate: true }
)

/**
 * 处理表单提交
 */
const handleSubmit = () => {
  if (props.isEdit) {
    // 编辑模式：只能修改价格、原价、折扣、结算底价、热门标签、隐藏状态、显示顺序
    const updateData: UpdatePricingRequest = {
      price: formData.value.price,
      originalPrice: formData.value.originalPrice,
      discountPercentage: formData.value.discountPercentage,
      settlementPrice: formData.value.settlementPrice,
      isHot: formData.value.isHot,
      isHidden: formData.value.isHidden,
      displayOrder: formData.value.displayOrder,
    }
    emit('submit', updateData)
  } else {
    // 创建模式：需要 planId
    if (!props.planId) {
      console.error('创建价格方案时必须提供 planId')
      return
    }
    const createData: CreatePricingRequest = {
      planId: props.planId,
      durationType: formData.value.durationType,
      durationDays: formData.value.durationDays,
      price: formData.value.price,
      originalPrice: formData.value.originalPrice,
      discountPercentage: formData.value.discountPercentage,
      settlementPrice: formData.value.settlementPrice,
      isHot: formData.value.isHot,
      isOneTime: formData.value.isOneTime,
      maxPurchaseCount: formData.value.maxPurchaseCount,
      displayOrder: formData.value.displayOrder,
      isHidden: formData.value.isHidden,
    }
    emit('submit', createData)
  }
}

/**
 * 处理取消操作
 */
const handleCancel = () => {
  emit('cancel')
}
</script>

<style scoped>
/* 可选：添加自定义样式 */
</style>
